/**
 * 服务方授权（PROVIDER CONSENT）。
 *
 * ══ 与「隐私与授权」（/profile/consent）的分工 ══
 *
 * 那一页管的是「**平台 / 专家 / AI** 能不能用某个数据域」，唯一键是 `(member_id, scope)`；
 * 这一页管的是「**哪一家机构**能读某个数据域」，唯一键是 `(member_id, provider_id, scope)`。
 * 两者必须能分别授权、分别撤回：一家疗养基地被授权读体检指标，不等于另一家体检机构也能读。
 *
 * ══ 本页的几条硬约束（数据与文案都以接口为准，界面不自行发明）══
 *
 *   1. **默认全关**：库里没有授权行就是未授权（服务端默认拒绝），所以开关初值一律是关；
 *      页面不提供「一键全开」，也不用红色与紧迫感文案催促授权。
 *   2. 每个数据域必须能回答三件事：**中文名**、**这家机构拿到它用来做什么**、
 *      **具体包含哪些字段** —— 字段清单逐字来自接口（`consents[].fields`），前端不另写一份。
 *   3. **授权可随时撤回、撤回后立即生效**：撤回后服务方下一次读取不再返回该域字段
 *      （服务端在未授权时连 SQL 都不执行）。已发生的读取记录不删除 —— 审计链只允许追加。
 *   4. 「谁看过我的数据」列出服务方的**每一次读取**（哪家机构 / 什么时间 / 哪个数据域）。
 *      这些是**事实记录、不可修改**；撤回授权只能停止后续读取，不能改写已经发生的事实。
 *   5. 未授权就显示「未授权」，**绝不写成 0 项 / 0 次**之类的数字。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConsentAuditEvent, ConsentScopeKey } from '@hew3/shared';
import { consentScopes } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { MISSING_TEXT, formatDateTimeOrMissing } from '../../lib/format.ts';
import { useUi } from '../../store/ui.ts';
import { Badge, Button, Card, StateBox, Switch } from '../../components/ui.tsx';

/* ------------------------------------------------------------------ *
 * 接口形状（以 apps/api/src/modules/provider.routes.ts 的实际响应为准）
 * ------------------------------------------------------------------ */

/** 单个数据域对某一家机构的授权状态。`fields` 是「授权后这家机构会看到的字段」的**唯一出处**。 */
interface ProviderScopeConsent {
  scope: ConsentScopeKey;
  label: string;
  desc: string;
  level: number;
  granted: boolean;
  updatedAt: string | null;
  fields: string[];
}

interface ProviderConsentGroup {
  providerId: string;
  providerName: string;
  subjectId: string;
  consents: ProviderScopeConsent[];
}

interface ProviderConsentListResponse {
  items: ProviderConsentGroup[];
  note: string;
}

interface ProviderConsentUpdateResponse {
  items: ProviderScopeConsent[];
  provider: { id: string; name: string };
}

interface AuditListResponse {
  items: ConsentAuditEvent[];
}

/** 审计查询单次上限（服务端限制 500）：本页据此说明「读取记录取自最近多少条审计」。 */
const AUDIT_SCAN_LIMIT = 500;

/* ------------------------------------------------------------------ *
 * 展示工具
 * ------------------------------------------------------------------ */

/**
 * 接口异常 → **照实展示服务端原文**（状态码 + message）。
 * 不改写成「网络异常」这类含糊说法：例如对 `research` 域授权时服务端回 400
 * 「该数据域不支持授权给服务方：research」，这句话本身才是成员需要看到的信息。
 */
function serverErrorText(error: unknown): string {
  if (error instanceof ApiError) return `HTTP ${error.status} · ${error.message}`;
  return `请求失败：${String(error)}`;
}

/**
 * 一条读取记录读的是哪个数据域。
 *
 * `scope` 为空表示这一次读取覆盖的是**履约必需信息**（姓名、电话、服务项目、时间、备注、状态）——
 * 服务端只在「读的是成员单独授权的数据域」时才写 scope，界面就如实这么说，
 * 不把它含糊成「其他」，也不安上一个它并不属于的数据域。
 */
function readScopeText(scope: ConsentAuditEvent['scope']): string {
  if (scope === null) return '履约必需信息（姓名、电话、服务项目、时间等，不含健康与财富数据域）';
  if (scope === 'account') return '账号信息';
  return consentScopes.find((s) => s.key === scope)?.label ?? scope;
}

interface ToggleVars {
  providerId: string;
  providerName: string;
  scope: ConsentScopeKey;
  label: string;
  granted: boolean;
}

export function ProviderConsentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useUi((s) => s.showToast);
  /** 上一次失败的授权操作：把服务端原文留在页面上（Toast 只显示 2.6 秒，不足以让人看清原因） */
  const [failure, setFailure] = useState<{ target: string; message: string } | null>(null);

  const consentsQuery = useQuery({
    queryKey: ['provider-consents'],
    queryFn: () => api.get<ProviderConsentListResponse>('/api/provider-consents'),
  });

  /*
   * 「谁看过我的数据」取自成员自助可查的审计接口（/api/consent/audit）。
   * 这里筛出 `actorType === 'provider' && action === 'read'`：服务方的状态变更、
   * 本人自己的操作都在「数据调用记录」那一屏里，不混进本区块。
   */
  const auditQuery = useQuery({
    queryKey: ['consent', 'audit', 'provider-reads'],
    queryFn: () => api.get<AuditListResponse>(`/api/consent/audit?limit=${AUDIT_SCAN_LIMIT}`),
  });

  const toggleConsent = useMutation({
    mutationFn: (vars: ToggleVars) =>
      api.put<ProviderConsentUpdateResponse>(`/api/provider-consents/${vars.providerId}/${vars.scope}`, {
        granted: vars.granted,
      }),
    onSuccess: (data, vars) => {
      setFailure(null);
      // 服务端返回的就是该机构最新的四域状态，直接采用它，不做前端推测
      queryClient.setQueryData<ProviderConsentListResponse>(['provider-consents'], (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((g) => (g.providerId === vars.providerId ? { ...g, consents: data.items } : g)),
            }
          : prev,
      );
      void queryClient.invalidateQueries({ queryKey: ['provider-consents'] });
      // 授权与撤回本身也会写入审计链，读取记录区块要同步刷新
      void queryClient.invalidateQueries({ queryKey: ['consent', 'audit'] });
      showToast(
        vars.granted
          ? `已授权「${vars.providerName}」读取${vars.label}，可随时撤回`
          : `已撤回「${vars.providerName}」的${vars.label}授权，撤回后立即生效`,
      );
    },
    onError: (error: unknown, vars) => {
      const message = serverErrorText(error);
      setFailure({ target: `${vars.providerName} · ${vars.label}`, message });
      showToast(message, 'error');
    },
  });

  const groups = consentsQuery.data?.items ?? [];
  const providerReads = (auditQuery.data?.items ?? []).filter(
    (e) => e.actorType === 'provider' && e.action === 'read',
  );

  /* ---------------- 首屏加载：骨架屏（三种状态都必须看得见，不能白屏） ---------------- */
  if (consentsQuery.isLoading) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            服务方授权<small>PROVIDER CONSENT</small>
          </div>
        </div>
        <div className="skeleton skel-card" />
        <div className="skeleton skel-card" style={{ height: 240 }} />
      </div>
    );
  }

  /* ---------------- 首屏失败：整页错误态 + 服务端原文（不展示残缺的开关） ---------------- */
  if (consentsQuery.isError && !consentsQuery.data) {
    return (
      <div className="page">
        <div className="scr-head">
          <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
            <svg viewBox="0 0 24 24">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <div className="scr-title">
            服务方授权<small>PROVIDER CONSENT</small>
          </div>
        </div>
        <StateBox
          kind="error"
          title="服务方授权状态加载失败"
          desc={`${serverErrorText(consentsQuery.error)}　授权状态读不到时，页面不展示开关 —— 否则您会以为「关着」就是「未授权」。`}
          action={<Button small onClick={() => void consentsQuery.refetch()}>重新加载</Button>}
        />
      </div>
    );
  }

  return (
    <div className="page">
      {/* ---------------- 返回头 ---------------- */}
      <div className="scr-head">
        <button type="button" className="back" onClick={() => navigate(-1)} aria-label="返回">
          <svg viewBox="0 0 24 24">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="scr-title">
          服务方授权<small>PROVIDER CONSENT</small>
        </div>
      </div>

      {/* ---------------- 默认口径（文案来自接口，不在这里另写一份） ---------------- */}
      <div className="note-src">
        <b>谁默认能看到什么</b>：{consentsQuery.data?.note || MISSING_TEXT}
      </div>

      <div className="note-src">
        <b>本页只放宽到一家机构</b>：下面每一个开关都只针对它所属的那一家服务方 ——
        打开某个数据域，只有这家机构在履约时能读到该域字段；关闭即撤回。
        <br />
        <b>授权可随时撤回；撤回后立即生效</b>：撤回后这家机构下一次读取就不再返回该域字段
        （未授权时服务端连该域的查询都不执行）。已经发生的读取记录不会被删除 ——
        审计链只允许追加，不允许改写。
      </div>

      {/* ---------------- 上一次操作失败（服务端原文） ---------------- */}
      {failure && (
        <>
          <StateBox
            kind="error"
            title={`授权未生效：${failure.target}`}
            desc={failure.message}
            action={<Button small onClick={() => setFailure(null)}>知道了</Button>}
          />
          <div className="sec-note">
            以上是服务端返回的原文。该数据域的授权状态没有被改动，页面显示的仍是接口当前状态。
          </div>
        </>
      )}

      {/* ---------------- 逐家机构、逐域授权 ---------------- */}
      {groups.length === 0 && (
        <StateBox
          title="暂无可授权的服务方"
          desc="服务方账号由平台在合作主体通过三级准入后开通；当前没有可授权的服务方，因此没有需要您决定的开关。"
        />
      )}

      {groups.map((group) => {
        const grantedItems = group.consents.filter((c) => c.granted);
        return (
          <Card
            key={group.providerId}
            title={group.providerName}
            more={
              <span className="card-more">
                {grantedItems.length > 0
                  ? `已授权：${grantedItems.map((c) => c.label).join(' · ')}`
                  : '当前未授权任何数据域'}
              </span>
            }
          >
            <div className="sec-note">
              以下开关<strong>默认全部关闭</strong>，需要您逐项决定。该机构受理并履约您的预约时，
              默认只能看到履约必需信息（姓名、电话、服务项目、时间、备注、状态）；
              健康与财富数据只有经您在此单独授权后才会下发。
            </div>

            {group.consents.length === 0 && (
              <StateBox title="接口未下发可授权数据域" desc={`字段清单：${MISSING_TEXT}（本机构暂无可授权数据域，界面不代为编造）`} />
            )}

            {group.consents.map((item) => {
              const busy = toggleConsent.isPending && toggleConsent.variables?.scope === item.scope
                && toggleConsent.variables?.providerId === group.providerId;
              return (
                <div
                  key={`${group.providerId}:${item.scope}`}
                  className="list-item"
                  style={{ alignItems: 'flex-start' }}
                >
                  <div className="li-main">
                    <div className="row-flex" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="li-t">{item.label}</span>
                      <Badge tone={item.granted ? 'ok' : 'ghost'}>
                        <span style={{ fontSize: 11 }}>{item.granted ? '已授权' : '未授权'}</span>
                      </Badge>
                      <span className="text-tiny text-muted">第 {item.level} 级</span>
                    </div>
                    {/* 用途：这一家机构拿到它用来做什么（服务端另有一份 dataSourceNote 逐字写着同一件事） */}
                    <div className="li-s">
                      用途：仅用于本次服务履约 —— 该机构受理并履约您的预约时，可在工单详情中查看下列字段；
                      平台的服务方接口不提供按成员检索与导出。
                    </div>
                    {item.desc && <div className="li-s">数据域口径：{item.desc}</div>}
                    <div className="li-s" style={{ marginTop: 6 }}>
                      授权后这家机构会看到的字段（清单来自接口）：
                    </div>
                    {item.fields.length === 0 ? (
                      <div className="li-s">字段清单：{MISSING_TEXT}（接口未下发字段清单）</div>
                    ) : (
                      <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                        {item.fields.map((field) => (
                          <li key={field} className="text-tiny text-muted" style={{ lineHeight: 1.6 }}>
                            {field}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="text-tiny text-muted" style={{ marginTop: 5 }}>
                      最近变更：{formatDateTimeOrMissing(item.updatedAt)}
                      {item.granted
                        ? '（已授权，可随时撤回）'
                        : item.updatedAt
                          ? '（已撤回，服务方读不到该域）'
                          : '（尚未授权过，服务方读不到该域）'}
                    </div>
                  </div>
                  <Switch
                    on={item.granted}
                    busy={busy}
                    label={`${group.providerName} · ${item.label}授权开关`}
                    onToggle={() =>
                      toggleConsent.mutate({
                        providerId: group.providerId,
                        providerName: group.providerName,
                        scope: item.scope,
                        label: item.label,
                        granted: !item.granted,
                      })
                    }
                  />
                </div>
              );
            })}
          </Card>
        );
      })}

      {/* ---------------- 谁看过我的数据 ---------------- */}
      <Card
        title="谁看过我的数据"
        more={
          providerReads.length > 0 ? <span className="card-more">共 {providerReads.length} 条读取记录</span> : undefined
        }
      >
        <div className="sec-note">
          下面列的是服务方（合作机构）对您数据的<strong>读取</strong>记录：哪家机构、什么时间、读取了哪个数据域。
          每一条都是<strong>事实记录，不可修改</strong>；撤回上面的授权可以停止<strong>后续</strong>读取，
          但不能删除已经发生的记录。（服务方发起的接单、开始、完成等状态变更，以及您本人的操作，
          都在「数据调用记录」中查看。）
        </div>

        {auditQuery.isLoading && <StateBox kind="loading" title="正在加载服务方读取记录…" />}

        {auditQuery.isError && (
          <StateBox
            kind="error"
            title="服务方读取记录加载失败"
            desc={`${serverErrorText(auditQuery.error)}　该区块是「谁看过我的数据」的唯一凭据，加载失败时请重试而不是忽略。`}
            action={<Button small onClick={() => void auditQuery.refetch()}>重新加载</Button>}
          />
        )}

        {auditQuery.data && providerReads.length === 0 && (
          <StateBox
            title="暂无服务方读取记录"
            desc="服务方每次读取您的履约信息或已授权数据域都会在此留痕；撤回授权可停止后续读取。"
          />
        )}

        {providerReads.map((event) => (
          <div key={event.id} className="list-item" style={{ alignItems: 'flex-start' }}>
            <div className="li-main">
              <div className="li-t">{event.actorName || MISSING_TEXT}</div>
              <div className="li-s">
                {formatDateTimeOrMissing(event.at)} · 读取数据域：{readScopeText(event.scope)}
              </div>
              <div className="li-s">服务端记录的用途：{event.purpose || MISSING_TEXT}</div>
              <div className="li-s">
                这是一个事实记录，不可修改；您可以通过撤回上面的授权停止这家机构后续的读取。
              </div>
            </div>
          </div>
        ))}

        <div className="sec-note">
          读取记录取自审计接口返回的最近 {AUDIT_SCAN_LIMIT} 条记录中「操作者类型为服务方、
          动作为读取」的条目；更早的记录可在「数据调用记录」中逐条查看。
        </div>
      </Card>

      <div className="sec-note text-center">
        授权与撤回都会写入哈希链审计 · 服务方默认只能看到履约必需信息
      </div>
    </div>
  );
}
